
// Vue, third party
import { Component, Watch, Vue, Mixins, Prop } from "vue-property-decorator";
import { State, Getter, Action, Mutation, namespace } from "vuex-class";

// Third Party Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";

// Sv Child components
import SureViewIcon from "@/components/SureViewIcon.vue";
import TaskTriggerDialog from "@/components/tasks/TaskTriggerDialog.vue";

// Mixins / Filters
import { parseTaskText } from "@/filters";
import ActionsMixin from "@/mixins/ActionsMixin";

// Types
import { TaskLibrary, TaskCategory, TaskType, ActionTrigger } from "@/store/tasks/types";
import { TreeView } from "@/types/treeView";
import { VueBootstrapField } from "@/store/types";
import { ActionsList } from "@/types/ActionList";

// Namespaces
const Tasks = namespace("tasks");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"sureview-icon": SureViewIcon,
		"task-trigger-dialog": TaskTriggerDialog
	},
	filters: {
		parseTaskText
	}
})
export default class ActionList extends Mixins(ActionsMixin) {
	@Tasks.Getter getActionById: (actionId: number) => TaskLibrary;
	@Tasks.Getter("getEditTriggerAction") triggerAction: any;
	@Tasks.Action loadEditTriggerAction: (action: TaskLibrary) => Promise<void>;

	/**
	 * Prop - contains the list of action libraries
	 */
	@Prop({ type: Array, default: () => [] }) actionLibrary: TaskLibrary[];

	/**
	 * Prop - contains the list of action categories
	 */
	@Prop({ type: Array, default: () => [] }) actionCategories: TaskCategory[];

	@Prop({ type: Array, default: () => [] }) actionTypes: TaskType[];

	@Prop({ type: Boolean, default: false }) disableActionEdit: boolean;

	@Prop({ type: Boolean, default: false }) disableActionDelete: boolean;

	@Prop({ type: Boolean, default: false }) disableBadges: boolean;

	@Prop({ type: Boolean, default: false }) enableRowCheckbox: boolean;

	@Prop({ type: Boolean, default: false }) disableActions: boolean;

	@Prop({ type: Boolean, default: true }) enableSearch: boolean;

	@Prop({ type: Boolean, default: true }) requireCondition: boolean;

	@Prop({ type: String, default: "" }) search: string;

	@Prop({ type: String, default: "" }) conditionText: string;

	@Prop({ type: Object, default: () => ({ height: "100%" }) }) pscroll: any;

	@Prop({ type: Boolean, default: false }) disableUsedItems: boolean;

	@Prop({ type: Boolean, default: false }) enableTriggerActions;

	/**
	 * The plan currently being edited
	 */
	@Prop() actionPlan: TreeView[];

	/**
	 * Array of all relevent triggers
	 */
	@Prop() actionTriggers: ActionTrigger[];

	@Prop({ type: Boolean, default: false }) isBusyLoading: boolean;

	private actions: ActionsList[] = [];

	/**
	 * Property to enable/disable the loading state for the table.
	 */
	private isBusy: boolean = true;

	private showTaskTriggerModal: boolean = false;

	/**
	 * Property to hold the bootstrap fields
	 */
	private fields: VueBootstrapField[] = [
		{ key: "check", label: "", sortable: false, class: `field-check ${this.enableRowCheckbox ? "" : "d-none"}` },
		{ key: "title", label: "Title", sortable: true },
		{ key: "category", label: "Category", sortable: true },
		{ key: "mobileSupported", label: "Supported", sortable: true },
		{ key: "actionType", label: "Action Type", sortable: true, class: "action-type-width" },
		{ key: "triggeredTaskCount", label: "Sub Actions", sortable: true, class: "trigger-count-width" },
		{ key: "actions", label: "Actions", sortable: false, class: `actions-width` }
	];

	/**
	 * @summary computed property to filter our fields before being used.
	 * @return filtered fields for the table.
	 */
	private get filteredFields(): VueBootstrapField[] {
		let fields = this.fields;
		if (this.disableActions) fields = fields.filter((field: VueBootstrapField) => field.key != "actions");
		return fields;
	}

	/**
	 * @summary helper method to get category by id
	 * @returns required action
	 */
	private getCategoryById(categoryActionId: number): TaskCategory | null {
		return this.actionCategories.find(x => x.taskCategoryID == categoryActionId);
	}

	private beforeDestroyed() {
		this.$emit("checked-actions", []);
		this.$emit("set-current-action-to-edit", null);
		this.$emit("set-current-action-to-delete", null);
	}

	/**
	 * @summary computed property to deconstruct the current modal and return the desired
	 * format modal.
	 * @return constructed list of actions for displaying within the table.
	 * @emits action-list full action list after reform
	 */
	private get filterActionsToTable(): ActionsList[] {
		if (!this.actionLibrary || this.actionLibrary.length == 0) {
			return [];
		}
		
		let actions: ActionsList[] = [];
		this.actionLibrary.forEach((action: TaskLibrary) => {
			const currentAction = this.actions ? this.actions.find(x => x.actionId == action.taskID) : null;
			actions.push({
				check: currentAction ? currentAction.check : false,
				actionId: action.taskID,
				title:
					!action.taskText || action.taskText == ""
						? this.$options.filters.parseTaskText(action)
						: action.taskText,
				category: this.getCategoryById(action.taskCategoryID)
					? this.getCategoryById(action.taskCategoryID).title
					: "",
				triggeredTaskCount: action.triggeredTaskCount,
				actionType: this.getTaskTypeById(action.taskTypeID)
					? this.getTaskTypeById(action.taskTypeID).title
					: "",
				condition: currentAction ? currentAction.condition : null,
				required: false,
				usedInCurrentPlan: this.actionUsedInCurrentPlan(action.taskID),
				usedAsChild: this.actionUsedAsChildAction(action.taskID),
				mobileSupported: this.actionIsMobileCompatible(action.taskTypeID)
			});
		});
		this.actions = actions;
		return actions;
	}

	/**
	 * @summary Watcher method to trigger a emit when the actions changed containing the checked actions
	 * @returns void
	 * @emits checked-actions emits a list of checked actions
	 */
	@Watch("actions", { deep: true })
	private emitCheckedActions(): void {
		this.$emit(
			"checked-actions",
			this.actions.filter(x => x.check)
		);
	}

	/**
	 * @summary Method to trigger event to set the current action
	 * @param actionId if the action object wasn't able to be sent, we can use the id to find the object.
	 * @event action contains the currently selected action to edit
	 * @return void
	 */
	private setCurrentActionToEdit(actionId: number): void {
		const action: TaskLibrary = this.getActionById(actionId);
		if (!action) return;
		this.$emit("set-current-action-to-edit", action);
	}

	private addSubAction(actionId: number): void {
		const action: TaskLibrary = this.getActionById(actionId);
		if (!action) return;
		this.$emit("add-sub-action", action);
	}

	/**
	 * @summary Method to trigger event to delete the selected action
	 * @param actionId if the action object wasn't able to be sent, we can use the id to find the object.
	 * @event action contains the currently selected action to delete
	 * @return void
	 */
	private setCurrentActionToDelete(actionId: number): void {
		const action: TaskLibrary = this.getActionById(actionId);

		if (!action) return;
		this.$emit("set-current-action-to-delete", action);
	}

	/**
	 * @summary Method to trigger event when search change
	 * @event search contains the string that the user has inputted
	 * @return void
	 * @emits search emits the current search value
	 */
	@Watch("search")
	private currentSearchText(): void {
		this.$emit("search", this.search);
	}

	/**
	 * @summary method to get the condition text, this is used for the placeholder
	 * @returns condition string
	 */
	private getCondition(context: any = null): string | null {
		if (this.conditionText) return this.conditionText;
		if (!context || !context.item || !context.item.title) return "";
		return `Condition for ${context.item.title}`;
	}

	public async setTriggerAction(actionId: number): Promise<void> {
		const action: TaskLibrary = this.actionLibrary.find((a: TaskLibrary) => a.taskID === actionId);
		await this.loadEditTriggerAction(action);
		this.showTaskTriggerModal = true;
	}

	private actionUsedInCurrentPlan(taskID: number): boolean {
		if (this.actionPlan) {
			return this.actionPlan.filter(task => task.id == taskID).length > 0;
		} else {
			return false;
		}
	}

	private actionUsedAsChildAction(taskID: number): boolean {
		if (this.actionTriggers) {
			return this.actionTriggers.filter(trigger => trigger.triggerTaskID == taskID).length > 0;
		} else {
			return false;
		}
	}

	private actionIsMobileCompatible(taskTypeID: number): boolean {
		if (this.actionTypes) {
			const type = this.actionTypes.find(type => type.taskTypeID == taskTypeID);
			return type ? type.mobileSupported : false;
		} else {
			return false;
		}
	}

	private showActionAsDisabled(action: ActionsList): boolean {
		return action.usedInCurrentPlan || action.usedAsChild;
	}

	private getToolTipText(action: ActionsList): string {
		let tooltipText: string = "";
		if (action.usedInCurrentPlan) {
			tooltipText = "Action used in current plan";
		} else if (action.usedAsChild) {
			tooltipText = "Action is a child action";
		}
		return tooltipText;
	}

	private setToolTip(action: ActionsList): Object {
		return {
			interactive: false,
			animation: false,
			placement: "auto",
			delay: 0,
			title: this.getToolTipText(action)
		};
	}

	private openTriggerActionDialog(): void {
		this.addSubAction(this.triggerAction.taskID);
		this.showTaskTriggerModal = true;
	}

	private closeTriggerActionDialog(): void {
		this.showTaskTriggerModal = false;
	}

	private setTriggerSubactionClass(action: ActionsList): string {
		if (this.showActionAsDisabled(action) || !this.enableTriggerActions) {
			return "disabled-badge";
		}

		return "bg-primary";
	}
}
